<template>
  <div>
    <template v-if="isTeacher">
      <h4>{{ isEdit ? "Modifier" : "Envoyer" }} un message</h4>
      <b-link
        :to="{ name: 'teachers.evaluations', params: { type: 'messages' } }"
        target="_blank"
        >Voir mes messages enregistrés
        <b-icon icon="exclamation-circle-fill" variant="primary"></b-icon>
      </b-link>
    </template>
    <template v-else>
      <h5 class="text-center">
        {{ isEdit ? "Modifier un message" : "Réponse au professeur" }}
      </h5>
    </template>
    <template v-if="messageToEdit.flagged">
      <b-icon icon="exclamation-circle-fill" variant="warning"></b-icon>
      <small>
        Votre publication est actuellement masqué pour tous. Veuillez svp la
        modifier tout en respectant les règles de Dlibr.</small
      >
    </template>
    <b-form
      @submit.stop.prevent="onSubmit"
      :class="isTeacher ? 'my-5' : 'my-3'"
    >
      <template v-if="isTeacher">
        <b-form-group>
          <b-form-select
            :options="preMsgs.data"
            text-field="message"
            value-field="id"
            v-model="selectedpreMsg"
          >
            <template v-slot:first>
              <b-form-select-option :value="null"
                >Choisir un message enregistré
                (facultatif)</b-form-select-option
              >
            </template>
          </b-form-select>
        </b-form-group>
      </template>
      <b-form-group>
        <b-form-textarea
          :disabled="Boolean(form.selectedpreMsg)"
          :placeholder="placeholder"
          rows="5"
          max-rows="6"
          v-model="form.message"
        ></b-form-textarea>
      </b-form-group>
      <template v-if="isTeacher">
        <b-form-group>
          <b-form-checkbox
            :disabled="Boolean(form.selectedpreMsg)"
            id="saveAsPredef"
            v-model="form.saveAsPredef"
            name="saveAsPredef"
          >
            Sauver comme nouveau message enregistré
          </b-form-checkbox>
        </b-form-group>
        <b-form-group class="text-right">
          <b-form-radio v-model="form.privacy" name="some-radios" :value="0">
            Privé (visible seulement pour l'étudiant)
          </b-form-radio>
          <b-form-radio v-model="form.privacy" name="some-radios" :value="1">
            Public (visible pour tous)
          </b-form-radio>
        </b-form-group>
      </template>
    </b-form>
    <div class="d-flex justify-content-between">
      <b-btn variant="light" pill @click="$emit('postSucceded')">
        Annuler
      </b-btn>
      <b-btn
        pill
        variant="primary"
        type="submit"
        @click="onSubmit"
        :disabled="messageCall == 'fetching'"
        >Envoyer
        <b-spinner class="ml-2" small v-if="messageCall == 'fetching'" />
      </b-btn>
    </div>
  </div>
</template>

<script>
/**
 * @file       Comment message component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import { mapState } from "vuex";
import { commentsMixin } from "@/modules/comments/mixins";

/**
 * Export the component.
 * 
 * @type  {Object}
 */
export default {
  name: "sendMessage",
  mixins: [commentsMixin],

  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      form: {
        selected: null,
        message: "",
        saveAsPredef: false,
        privacy: 0
      }
    };
  },
  mounted() {
    if (this.isTeacher) {
      this.$store.commit("messages/setMessagesFilters", {
        reset: true,
        fields: {
          resourcesPerPage: 25,
          id: this.$route.params.teacher
        }
      });
      this.$store.dispatch("messages/getMessages");
    }
    if (this.isEdit) {
      if (this.messageToEdit.predefined_message) {
        this.form.selectedpreMsg = this.messageToEdit.predefined_message.id;
      } else {
        this.form.message = this.messageToEdit.message;
      }
      this.form.privacy = this.messageToEdit.public;
    }
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    ...mapState({
      preMsgs: state => state.messages.messages.data,
      messageCall: state => state.comments.message.status
    }),

    /**
     * Is edit.
     * 
     * @return  {Bool}   true or false
     */
    isEdit() {
      return Boolean(this.messageToEdit.isEdit);
    },

    /**
     * Format comment.
     * 
     * @return  {Object}   the comment
     */
    formatPayload() {
      const payload = {};
      if (this.isEdit) {
        payload.message_id = this.messageToEdit.id;
      } else {
        payload.comment = this.commentToEdit.pub.id;
        payload.user = this.profile.id;
      }
      payload.public = this.isTeacher
        ? Boolean(this.form.privacy)
        : Boolean(this.messageToEdit.public);
      if (this.isStudent) {
        payload.message_to = this.messageToEdit.id;
      }
      if (this.form.selectedpreMsg) {
        payload.predefined_message = this.form.selectedpreMsg;
      } else {
        payload.message = this.form.message;
      }
      return payload;
    },

    /**
     * Get placeholder text.
     * 
     * @return  {String}   the placeholder text
     */
    placeholder() {
      return this.isTeacher
        ? "Écrivez à l'étudiant..."
        : "Saississez un message...";
    },

    /**
     * Selected Pre Message.
     */
    selectedpreMsg: {
      set(value) {
        if (value) {
          this.form.selected = value;
          this.form.message = this.preMsgs.data.find(
            p => p.id === value
          ).message;
        } else {
          this.form.selected = null;
          this.form.message = null;
        }
      },
      get() {
        return this.form.selected;
      }
    }
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    /**
     * Called on submit comment.
     */
    onSubmit() {
      if (!this.form.selectedpreMsg && this.form.message === "") return;
      this.isEdit ? this.update() : this.create();
    },

    /**
     * Called on update comment.
     */
    update() {
      this.$store
        .dispatch("comments/putMessage", this.formatPayload)
        .then(() => {
          if (this.form.saveAsPredef) {
            this.$store.dispatch("messages/postMsg", {
              message: this.form.message
            });
          }
          this.$emit("postSucceded");
          this.updateStetaComment().then(() => {
            this.$root.$bvToast.toast(`Message mis à jour avec succès`, {
              title: "Succès"
            });
          });
        });
    },

    /**
     * Called on create comment.
     */
    create() {
      this.$store
        .dispatch("comments/postMessage", this.formatPayload)
        .then(() => {
          if (this.form.saveAsPredef) {
            this.$store.dispatch("messages/postMsg", {
              message: this.form.message
            });
          }
          this.$emit("postSucceded");
          this.updateStetaComment().then(() => {
            this.$root.$bvToast.toast(`Message créé avec succès`, {
              title: "Succès"
            });
          });
        });
    }
  }
};
</script>

<style lang="scss" scoped></style>
